/* Where the workspace is, as its address says (frontend/README.md › Addresses has the table):
   the page, the agent it is about and the ids below it. `placeOf` reads a path, `pathOf` writes
   one, and `pathOfHash` turns an old `#/view/agent/…` link into a path. Those links live on in
   bookmarks and in the backend's notices (backend/modules/notifications/business/policy.py), so
   the hash is still read. */
import type { View } from "@/shared/types";

/** Ids are optional, and null where a page holds "none" (Spaces' table and row). */
export interface Place {
  view: View;
  /** The agent an agent page is about: `BUILDER` on `/builder` until Definable's id is read. */
  agentId?: string;
  chatId?: string | null;
  /** Agent Settings' section, or workspace Settings' on `/settings/:section`. */
  section?: string | null;
  kbId?: string | null;
  nodeId?: string | null;
  tableId?: string | null;
  rowId?: string | null;
  runId?: string | null;
}

/** The chat pane a history entry opens: each New chat is its own (`new-<n>`), and keeps it when
    its first message names the chat; any chat opened is the one "open" pane. */
export type Pane = "open" | "new" | `new-${number}`;

/** The pane an entry's state names, if it names one. */
export const paneOf = (state: unknown): Pane | undefined =>
  (state as { pane?: Pane } | null)?.pane;

/** `/builder`'s agent before Definable's id is known. */
export const BUILDER = "builder";

/** Workspace pages whose path is just their name (Knowledge and Settings carry ids). */
const NAMED_PAGES: View[] = ["apps", "skills", "templates", "schedule", "archive"];
/** An agent's pages: its tabs, in their order (`TopBar`). */
export const AGENT_PAGES: View[] = ["chat", "traces", "evals", "reports", "tasks", "spaces", "interfaces", "settings"];
/** The ids an address can carry below an agent page. */
type IdName = "chatId" | "section" | "runId" | "tableId" | "rowId";
/** The ids under an agent page, in path order: a later one needs the one before it. */
const IDS_UNDER: Partial<Record<View, IdName[]>> = {
  chat: ["chatId"],
  settings: ["section"],
  traces: ["runId"],
  spaces: ["tableId", "rowId"],
};

/** An agent page's place from the segments after the page's name. */
function agentPlace(view: View, agentId: string | undefined, ids: (string | undefined)[]): Place {
  const place: Place = { view, agentId };
  for (const [i, name] of (IDS_UNDER[view] ?? []).entries()) {
    if (!ids[i]) break; // a row needs its table
    place[name] = ids[i];
  }
  return place;
}

/** A path's segments, decoded; none for one that can't be (a pasted `%E0`). */
function segmentsOf(pathname: string): string[] {
  try {
    return pathname.split("/").filter(Boolean).map(decodeURIComponent);
  } catch {
    return []; // malformed percent-encoding: Overview, not a crash
  }
}

/** The place a path names; one it doesn't know is Overview. */
export function placeOf(pathname: string): Place {
  const [first, second, third, fourth, fifth] = segmentsOf(pathname);
  if (!first) return { view: "overview" };
  if (first === "builder") return { view: "chat", agentId: BUILDER, chatId: second === "chats" ? third : undefined };
  if (first === "settings") return { view: "workspace-settings", section: second };
  if (first === "knowledge") return { view: "knowledge", kbId: second, nodeId: second ? third : undefined };
  if (NAMED_PAGES.includes(first as View)) return { view: first as View };
  if (first !== "agents" || !second) return { view: "overview" };
  const view = (third ?? "chat") as View;
  if (!AGENT_PAGES.includes(view)) return { view: "overview" };
  return agentPlace(view, second, [fourth, fifth]);
}

/** `/a/b` for the parts given, up to the first missing one: a row needs its table. */
function tail(...parts: (string | null | undefined)[]): string {
  let path = "";
  for (const part of parts) {
    if (!part) break;
    path += `/${encodeURIComponent(part)}`;
  }
  return path;
}

/** The path for a place. `builderId`: Definable's agent, whose chat is `/builder`. */
export function pathOf(place: Place, builderId?: string): string {
  const { view, agentId } = place;
  if (view === "overview") return "/";
  if (view === "workspace-settings") return `/settings${tail(place.section)}`;
  if (view === "knowledge") return `/knowledge${tail(place.kbId, place.nodeId)}`;
  if (NAMED_PAGES.includes(view)) return `/${view}`;
  if (!agentId) return "/";
  // Definable has only its chat
  if (agentId === BUILDER || agentId === builderId) return place.chatId ? `/builder/chats/${encodeURIComponent(place.chatId)}` : "/builder";
  return `/agents/${encodeURIComponent(agentId)}/${view}${tail(...(IDS_UNDER[view] ?? []).map((name) => place[name]))}`;
}

const OLD_VIEWS: View[] = ["overview", ...NAMED_PAGES, "knowledge", "workspace-settings", ...AGENT_PAGES];

/** The path an old `#/view/agentId/…` link meant, or null for a hash that is no such link.
    `#/threads/…` was the chat, `#/onboarding/:agentId` is Settings › Onboarding now, and every
    workspace page carried the agent that was open, which a path no longer needs. */
export function pathOfHash(hash: string): string | null {
  const m = hash.match(/^#\/([a-z-]+)(?:\/([a-z0-9-]+))?(?:\/([a-z0-9-]+))?(?:\/([a-z0-9-]+))?/);
  if (!m) return null;
  const [, name, first, second, third] = m;
  if (name === "onboarding") return first ? pathOf({ view: "settings", agentId: first, section: "onboarding" }) : "/";
  const view = (name === "threads" ? "chat" : name) as View;
  if (!OLD_VIEWS.includes(view)) return "/";
  if (view === "workspace-settings") return pathOf({ view, section: first });
  if (view === "knowledge") return pathOf({ view, kbId: first, nodeId: second });
  if (!AGENT_PAGES.includes(view)) return pathOf({ view });
  return pathOf({
    view,
    agentId: first,
    chatId: view === "chat" ? second : undefined,
    section: view === "settings" ? second : undefined,
    tableId: view === "spaces" ? second : undefined,
    rowId: view === "spaces" ? third : undefined,
  });
}
